// uiContextSlice — opt-in, page-supplied UI context for chat grounding.
//
// Two public read APIs — pick one based on your need:
//
// 1. readUiContext()  -> one-shot imperative read (no subscription, no re-render)
//                        Use from event handlers: send buttons, submit callbacks, etc.
//                        Chat panel handleSend/handleConfirm use this.
//
// 2. useUiContext()   -> reactive subscription (component re-renders on change)
//                        Use from render paths where the UI must update:
//                        context badges, notification strips, preview modals, etc.
//
// Both are public and supported. Pick the one that matches your intent — the
// name makes the choice self-documenting at the call site.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import store, { type RootState } from '../index';

export interface UiContextState {
  activeEntityType: string | null;
  activeEntityId: string | null;
  selectedEntityType: string | null;
  selectedEntityIds: string[];
  selectionFilterSummary: string | null;
}

const initialState: UiContextState = {
  activeEntityType: null,
  activeEntityId: null,
  selectedEntityType: null,
  selectedEntityIds: [],
  selectionFilterSummary: null,
};

const uiContextSlice = createSlice({
  name: 'uiContext',
  initialState,
  reducers: {
    setActiveEntity(
      state,
      action: PayloadAction<{ type: string | null; id: string | null }>,
    ) {
      state.activeEntityType = action.payload.type;
      state.activeEntityId = action.payload.id;
    },
    clearActiveEntity(state) {
      state.activeEntityType = null;
      state.activeEntityId = null;
    },
    setSelection(
      state,
      action: PayloadAction<{
        type: string | null;
        ids: string[];
        filter?: string | null;
      }>,
    ) {
      state.selectedEntityType = action.payload.type;
      state.selectedEntityIds = action.payload.ids;
      state.selectionFilterSummary = action.payload.filter ?? null;
    },
    clearSelection(state) {
      state.selectedEntityType = null;
      state.selectedEntityIds = [];
      state.selectionFilterSummary = null;
    },
    clearUiContext() {
      return initialState;
    },
  },
});

export const {
  setActiveEntity,
  clearActiveEntity,
  setSelection,
  clearSelection,
  clearUiContext,
} = uiContextSlice.actions;

// ---------- Public read API #1: reactive ----------
/** Reactive subscription — use when your component's render depends on UI context. */
export const useUiContext = (): UiContextState =>
  useSelector((state: RootState) => state.uiContext);

// ---------- Public read API #2: imperative ----------
/** One-shot read — use from event handlers when you need the current value but not a subscription. */
export const readUiContext = (): UiContextState =>
  store.getState().uiContext;

export default uiContextSlice.reducer;
